import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaperclip, faTimes, faExclamationCircle, faQuestionCircle } from "@fortawesome/pro-regular-svg-icons";
import { useDropzone } from "react-dropzone";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";

export default function BookingFileUpload({ fileTypes = [], ...props }) {
    const [files, setFiles] = useState([]);
    const [fileTypeSelections, setFileTypeSelections] = useState({});
    const [missingRequired, setMissingRequired] = useState([]);

    // Reset internal state when parent requests a reset (e.g., after successful send)
    useEffect(() => {
        if (props.resetSignal !== undefined) {
            setFiles([]);
            setFileTypeSelections({});
            setMissingRequired([]);
        }
    }, [props.resetSignal]);

    const onDrop = React.useCallback((acceptedFiles) => {
        // Initialize file type selections for new files
        const newSelections = {};
        acceptedFiles.forEach(file => {
            newSelections[file.name] = ''; // Default to empty selection
        });
        
        setFileTypeSelections(prev => ({
            ...prev,
            ...newSelections
        }));
        
        setFiles((prev) => [...prev, ...acceptedFiles]);
        
        // Check for required file types that are missing
        checkMissingRequiredFiles([...files, ...acceptedFiles], fileTypeSelections);
    }, [files, fileTypeSelections]);

    useEffect(() => {
        // Combine files with their selected file types
        const filesWithTypes = files.map(file => ({
            file,
            file_type_id: fileTypeSelections[file.name] || null
        }));
        
        props.setFieldValue("files", filesWithTypes);
        props.setFieldValue("template", 0);
    }, [files, fileTypeSelections]);

    const { getRootProps, getInputProps } = useDropzone({
        maxFiles: props.max_files,
        onDrop,
    });
    
    const handleFileTypeChange = (fileName, fileTypeId) => {
        const newSelections = {
            ...fileTypeSelections,
            [fileName]: fileTypeId
        };
        
        setFileTypeSelections(newSelections);
        
        // Check for required file types that are missing
        checkMissingRequiredFiles(files, newSelections);
    };
    
    // Check for required file types that are missing
    const checkMissingRequiredFiles = (currentFiles, currentSelections) => {
        if (!props.fileTypes || props.fileTypes.length === 0) return;
        
        // Get all required file type IDs
        const requiredFileTypeIds = [];
        props.fileTypes.forEach(group => {
            if (group.file_types) {
                group.file_types.forEach(fileType => {
                    if (fileType.required) {
                        requiredFileTypeIds.push(fileType.id.toString());
                    }
                });
            }
        });
        
        // Get the IDs of the currently selected file types
        const selectedFileTypeIds = Object.values(currentSelections)
            .filter(id => id && id !== '')
            .map(id => id.toString());
        
        // Find required file types that are not yet selected
        const missing = requiredFileTypeIds.filter(id => 
            !selectedFileTypeIds.includes(id)
        );
        
        // Get the names of the missing required file types
        const missingNames = [];
        props.fileTypes.forEach(group => {
            if (group.file_types) {
                group.file_types.forEach(fileType => {
                    if (missing.includes(fileType.id.toString())) {
                        missingNames.push(fileType.name);
                    }
                });
            }
        });
        
        setMissingRequired(missingNames);
    };

    // Check if there are required file types that are missing
    const hasMissingRequired = missingRequired.length > 0;
    
    return (
        <div className="relative">
            <div {...getRootProps()}>
                <input {...getInputProps()} />
                <button
                    type="button"
                    className="relative inline-block text-white bg-primary px-2 py-1 rounded-xl hover:bg-primary_hover transition duration-150 ease-in-ease-out hover:-translate-y-0.5"
                >
                    <FontAwesomeIcon className="h-4 w-4" aria-hidden="true" icon={faPaperclip} />
                </button>
            </div>
            
            {hasMissingRequired && (
                <div className="absolute top-full left-0 mt-1 flex items-center text-yellow-400 text-xs whitespace-nowrap">
                    <FontAwesomeIcon icon={faExclamationCircle} className="mr-1" />
                    <span>Required: {missingRequired.join(', ')}</span>
                </div>
            )}
            
            {files.length > 0 && (
                <div className="absolute top-full left-0 mt-1 flex flex-wrap gap-2 z-10">
                    {files.map((file, i) => (
                        <div key={file.name} className="inline-flex items-center gap-2 text-white bg-gray-700 px-3 py-1.5 rounded-md text-sm shadow-lg">
                            <span className="truncate max-w-[200px]">
                                {file.name}
                            </span>
                            <span className="text-gray-300 text-xs">
                                ({(file.size / 1024).toFixed(1)} KB)
                            </span>
                            <button
                                type="button"
                                onClick={() => {
                                    const newFiles = [...files];
                                    newFiles.splice(i, 1);
                                    
                                    // Remove the file type selection
                                    const newSelections = {...fileTypeSelections};
                                    delete newSelections[file.name];
                                    setFileTypeSelections(newSelections);
                                    
                                    setFiles(newFiles);
                                }}
                                className="text-red-400 hover:text-red-300 transition-colors"
                                title="Remove"
                            >
                                <FontAwesomeIcon icon={faTimes} className="h-3 w-3" />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
